import {
  Flex,
  Text,
  Badge,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Box
} from '@chakra-ui/react';
import { IPeerReviewListItem } from '@publishing-engine/domain/views';
import { IReviewer } from '@publishing-engine/domain/models';
import { renderReviewStatusColumn } from './ReadOnlyTableColumns';
import { getReviewerNameOrEmail } from 'src/utils/reviews';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPeopleArrows } from '@fortawesome/pro-regular-svg-icons';
import { faUserTag } from '@fortawesome/pro-solid-svg-icons';
import { Tooltip } from '@chakra-ui/tooltip';
import { recommendationBadges } from '../../Review/Badges';
import { ButtonText } from '@morressier/alembic';
import UserName from 'src/components/User/UserName';
import { IAuthor } from '@publishing-engine/domain/types/AuthorType';
import {
  ConsentToReInviteIcon,
  resolveRoundConsentToReInvite
} from './RevisionReviewsTable';
import { REVIEW_STATUS_MAP, reviewedStatus } from 'src/constants';
import { useSubmissionReviewsData } from 'src/contexts/SubmissionReviewsDataProvider';

interface TableColumn {
  key: string;
  label: string;
  size: string | number;
  render?: (review: IPeerReviewListItem) => React.ReactNode;
}

interface ReviewsHistoryTable {
  reviews: IPeerReviewListItem[];
  onSelectedAuthor: (author: IReviewer | null) => void;
  onSelectReviewId: (reviewId: string) => void;
}

/**
 * The review states whose Status cell is the `reviewedStatus` badge, taken from
 * the map the Status column renders from so the two cannot drift: these are the
 * rows that carry a review. Matching on the badge rather than on its label keeps
 * the set stable if "Reviewed" is ever reworded. Every other status means the
 * reviewer is mid-assessment, has not been asked yet, or left the round —
 * declined, withdrawn, expired — and none of those reads a recommendation.
 */
const REVIEWED_REVIEW_STATES: string[] = Object.entries(REVIEW_STATUS_MAP)
  .filter(([, status]) => status === reviewedStatus)
  .map(([state]) => state);

/**
 * The Recommendation cell: the verdict this reviewer gave, read from the
 * review's own assessments rather than from the round the table lists. A
 * reviewer invited for the first time can only ever hold a first assessment —
 * submitting a revision assessment requires being reinvited for a revision
 * round (`revisionInvited` → `revisionAssessment` → `SubmitRevisionAssessment`)
 * — so a review carrying a revision assessment was reinvited and that is its
 * latest verdict, while a review without one keeps the verdict it gave in round
 * 1. That is the same rule the API follows for `latestRecommendation`, taken
 * over the first, the archived and the current revision assessment.
 */
export const renderRecommendationColumn = (review: IPeerReviewListItem) => {
  if (!REVIEWED_REVIEW_STATES.includes(review.workflow.currentState)) {
    return 'N/A';
  }

  const { firstAssessment, revisionAssessment } = review.caseData;
  const recommendation =
    revisionAssessment?.recommendation ?? firstAssessment?.recommendation;

  return recommendation ? recommendationBadges[recommendation] : 'N/A';
};

/**
 * The Reviewer cell: the name — which opens the reviewer's profile — carrying
 * the email-delivery state, then the consent-to-re-invite and board-member
 * badges. It is rendered by the row rather than through `TableColumn.render`
 * because it needs the author profile the row fetches.
 */
const ReviewerCell = ({
  review,
  isRevisionRound
}: {
  review: IPeerReviewListItem;
  isRevisionRound: boolean;
  authorData?: IAuthor;
}) => (
  <Flex flexDirection="column" alignItems="flex-start" gap={2}>
    <Box>
      <Flex align={'center'} gap={2}>
        <UserName
          firstName={getReviewerNameOrEmail(review.reviewer)}
          tooltipLabel={'View Reviewer Profile'}
        />
        <ConsentToReInviteIcon
          consentToReInvite={resolveRoundConsentToReInvite(
            review,
            isRevisionRound
          )}
          currentState={review.workflow.currentState}
        />
        {review.isBoardMember && (
          <Tooltip placement="top" label="Board Member" shouldWrapChildren>
            <Badge variant="neutral" cursor={'pointer'} size="context">
              <FontAwesomeIcon size="sm" icon={faUserTag} />
            </Badge>
          </Tooltip>
        )}
      </Flex>
      <Text textStyle="textContext">
        {review.reviewer.affiliationDetails?.organizationName}
      </Text>
    </Box>

    {review.coReview && review.coReview.status === 'accepted' && (
      <>
        <Box>
          <Text textStyle="headingItem">
            {getReviewerNameOrEmail(review.coReview.coReviewer)}
          </Text>
          {review.coReview.coReviewer.affiliation && (
            <Text textStyle="textContext">
              {review.coReview.coReviewer.affiliation}
            </Text>
          )}
        </Box>
        <Badge variant="attention" size="context">
          <FontAwesomeIcon icon={faPeopleArrows} />
          Co-Reviewer
        </Badge>
      </>
    )}
  </Flex>
);

const getReviewTableColumns = (): TableColumn[] => [
  {
    key: 'reviewer',
    label: 'Reviewer',
    size: 1
  },
  {
    key: 'status',
    label: 'Status',
    size: 1,
    render: renderReviewStatusColumn
  },
  {
    key: 'recommendation',
    label: 'Recommendation',
    size: 1,
    render: review => <Box>{renderRecommendationColumn(review)}</Box>
  }
];

const TableHeader = ({ columns }: { columns: TableColumn[] }) => (
  <Thead>
    <Tr
      gap={4}
      sx={{
        '& th': {
          textTransform: 'none',
          color: 'black',
          background: 'white'
        }
      }}
    >
      {columns.map(column => (
        <Th key={column.key} flex={column.size} textAlign="left">
          <Text textStyle="headingItem" whiteSpace="nowrap">
            {column.label}
          </Text>
        </Th>
      ))}
      <Th key="actions" textAlign="center" />
    </Tr>
  </Thead>
);

/**
 * One history row. The author profile is fetched per row, the same query the
 * live reviews table's row makes, because the Reviewer cell needs it for the
 * email-delivery indicator and the proxy control.
 */
const ReviewsHistoryRow = ({
  review,
  isLastRow,
  columns,
  isRevisionRound,
  onSelectedAuthor,
  onSelectReviewId
}: {
  review: IPeerReviewListItem;
  isLastRow: boolean;
  columns: TableColumn[];
  isRevisionRound: boolean;
  onSelectedAuthor: (author: IReviewer | null) => void;
  onSelectReviewId: (reviewId: string) => void;
}) => {
  // A row with a verdict has a review to open, and one without does not, so
  // "View Review" follows the same rule as the Recommendation cell.
  const isReviewed = renderRecommendationColumn(review) !== 'N/A';

  return (
    <Tr
      style={{
        borderBottom: isLastRow ? 'none' : undefined
      }}
      background="white"
    >
      {columns.map(column => (
        <Td
          key={column.key}
          flex={column.size}
          textAlign="left"
          cursor={column.key === 'reviewer' ? 'pointer' : undefined}
          _hover={{
            bg: column.key === 'reviewer' ? 'gray.50' : undefined
          }}
          onClick={event => {
            if (column.key === 'reviewer') {
              event.stopPropagation();
              onSelectedAuthor(review.reviewer);
            }
          }}
        >
          {column.key === 'reviewer' ? (
            <ReviewerCell
              review={review}
              isRevisionRound={isRevisionRound}
            />
          ) : column.render ? (
            column.render(review)
          ) : null}
        </Td>
      ))}
      <Td textAlign="right">
        <ButtonText
          alignSelf="center"
          isDisabled={!isReviewed}
          onClick={() => onSelectReviewId(review.id)}
        >
          View Review
        </ButtonText>
      </Td>
    </Tr>
  );
};

const ReviewsHistoryTable = ({
  reviews,
  onSelectReviewId,
  onSelectedAuthor
}: ReviewsHistoryTable) => {
  const columns = getReviewTableColumns();
  // The round these rows were fetched for, so each cell reads that round's own
  // assessment rather than one from an earlier round.
  const { isRevisionRound } = useSubmissionReviewsData();

  return (
    <Table>
      <TableHeader columns={columns} />
      <Tbody>
        {reviews.map((review, index) => (
          <ReviewsHistoryRow
            key={index}
            review={review}
            columns={columns}
            isLastRow={index === reviews.length - 1}
            isRevisionRound={isRevisionRound}
            onSelectedAuthor={onSelectedAuthor}
            onSelectReviewId={onSelectReviewId}
          />
        ))}
      </Tbody>
    </Table>
  );
};

export default ReviewsHistoryTable;
